import { INTERNAL_USER_POOL_STORAGE_KEY } from "./constants";
import { ROUTES } from "./navigationUtils";

const SUPPORTED_SIGN_IN_PATHS = ["/changePassword"];

/**
 * Returns true when a session was established against the internal user pool.
 * The flag is persisted so the whole browser session (including OAuth redirects
 * and page reloads) keeps using the internal pool.
 */
export const isInternalUserPoolSession = (): boolean =>
  window.localStorage.getItem(INTERNAL_USER_POOL_STORAGE_KEY) === "true";

/**
 * Persists (or clears) the internal user pool session flag.
 */
export const setInternalUserPoolSession = (value: boolean): void => {
  if (value) {
    window.localStorage.setItem(INTERNAL_USER_POOL_STORAGE_KEY, "true");
  } else {
    window.localStorage.removeItem(INTERNAL_USER_POOL_STORAGE_KEY);
  }
};

const isInternalLoginPath = (): boolean => window.location.pathname.split("/").includes(ROUTES.INTERNAL_LOGIN);

/**
 * Determines whether Amplify should be configured against the internal user pool.
 * True when the current path is the internal login page or when the session was
 * already established against the internal pool.
 */
export const isInternalUserPool = (): boolean => isInternalLoginPath() || isInternalUserPoolSession();

/**
 * Builds the Amplify configuration, selecting the internal or main user pool.
 */
export const getAmplifyConfig = () => {
  const internal = isInternalUserPool();
  return {
    aws_user_pools_id: internal ? import.meta.env.VITE_INTERNAL_USERPOOL_ID : import.meta.env.VITE_USERPOOL_ID,
    aws_user_pools_web_client_id: internal
      ? import.meta.env.VITE_INTERNAL_USERPOOL_ID_WEBCLIENT_ID
      : import.meta.env.VITE_USERPOOL_WEBCLIENT_ID,
    oauth: {
      domain: internal ? import.meta.env.VITE_INTERNAL_USERPOOL_OAUTH_DOMAIN : import.meta.env.VITE_OAUTH_DOMAIN,
      scope: ["email", "openid", "profile", "aws.cognito.signin.user.admin"],
      redirectSignIn: SUPPORTED_SIGN_IN_PATHS.includes(window.location.pathname)
        ? `${window.location.origin}${window.location.pathname}`
        : `${window.location.origin}/`,
      redirectSignOut: `${window.location.origin}/`,
      responseType: "code"
    },
    federationTarget: "COGNITO_USER_POOLS"
  };
};
